/*
 * Joon Custom Cart Drawer
 * ------------------------------------------------------------------
 * Original, dependency-free slide-out cart for Joon.
 * No third-party cart platform (no GoKwik). Talks to Shopify's native
 * AJAX Cart API and is locale/markets-safe by reading the theme's own
 * `theme.routes` when present.
 * Config is injected by sections/joon-cart-drawer.liquid via a JSON
 * <script> tag so everything is editable from the Theme Editor.
 * ------------------------------------------------------------------
 */
(function () {
  "use strict";

  var ROOT_ID = "joon-cart-drawer";

  /* ---------------------------- utilities ---------------------------- */

  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $all(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  function readConfig() {
    var el = document.getElementById("joon-cart-drawer-config");
    if (!el) return {};
    try { return JSON.parse(el.textContent); } catch (e) { return {}; }
  }

  // Locale/markets-safe cart routes: prefer the theme's own route table.
  function routes() {
    var r = (window.theme && window.theme.routes) ? window.theme.routes : {};
    return {
      cart:   r.cart_url        || "/cart",
      add:   (r.cart_add_url    || "/cart/add") + ".js",
      change:(r.cart_change_url || "/cart/change") + ".js",
      update:(r.cart_update_url ? String(r.cart_update_url).replace(/\.js$/, "") : "/cart/update") + ".js",
      get:   (r.cart_url        || "/cart") + ".js",
      checkout: "/checkout"
    };
  }

  function formatMoney(cents, cfg) {
    if (window.Shopify && typeof window.Shopify.formatMoney === "function") {
      try { return window.Shopify.formatMoney(cents, cfg.moneyFormat); } catch (e) {}
    }
    if (window.theme && window.theme.Shopify && typeof window.theme.Shopify.formatMoney === "function") {
      try { return window.theme.Shopify.formatMoney(cents, cfg.moneyFormat); } catch (e) {}
    }
    var value = (cents / 100);
    var str = value.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    var fmt = cfg.moneyFormat || "₹{{amount}}";
    return fmt
      .replace(/\{\{\s*amount\s*\}\}/g, str)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, Math.round(value).toLocaleString("en-IN"))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, str);
  }

  function esc(s) {
    return String(s == null ? "" : s)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;")
      .replace(/>/g, "&gt;").replace(/"/g, "&quot;");
  }

  function sized(url, w) {
    if (!url) return "";
    try { return url.replace(/(\.(?:png|jpe?g|webp|gif))(\?.*)?$/i, "_" + (w || 160) + "x$1$2"); }
    catch (e) { return url; }
  }

  // Is this an add-to-cart form? (covers Shopify product forms + this theme's
  // .product-purchase-form, including engraving/custom-option inputs attached
  // via the HTML `form=` attribute — FormData(form) picks those up too.)
  function isAddForm(form) {
    if (!form || form.tagName !== "FORM") return false;
    var action = form.getAttribute("action") || "";
    if (/\/cart\/add/.test(action)) return true;
    if (form.classList && form.classList.contains("product-purchase-form")) return true;
    if (form.querySelector('[name="id"]') && /\/cart(\/|$|\?)/.test(action)) return true;
    return false;
  }

  // Reference (pre-discount) unit price: the largest of compare-at (MRP) and
  // the original price before line/cart discounts. Falls back to final price.
  function refPrice(item) {
    return Math.max(
      item.compare_at_price || 0,
      item.original_price || 0,
      item.final_price || item.price || 0
    );
  }

  /* ------------------------------ class ------------------------------ */

  function JoonCartDrawer(root, cfg) {
    this.root = root;
    this.cfg = cfg;
    this.R = routes();
    this.cart = null;
    this.busy = false;
    this._selfAdd = false; // guard so our own add doesn't double-open
    this.itemsEl = $(".jcd__items", root);
    this.progressEl = $(".jcd__progress", root);
    this.bind();
    this.patchNetwork();
    this.patchFormSubmit();
    this.refresh();
  }

  // Some code paths (this theme / GoKwik) call form.submit() programmatically,
  // which does NOT fire a 'submit' event and would do a full-page POST to
  // /cart/add and then redirect to /cart. Intercept it so the drawer stays.
  JoonCartDrawer.prototype.patchFormSubmit = function () {
    var self = this;
    var proto = HTMLFormElement.prototype;
    if (proto.__jcdSubmitPatched) return;
    proto.__jcdSubmitPatched = true;
    var _submit = proto.submit;
    proto.submit = function () {
      try {
        if (self.cfg.enabled !== false && isAddForm(this)) {
          // If we just handled this add via the submit event, don't add again.
          if (Date.now() - (this.__jcdAt || 0) > 2500) self.addFromForm(this);
          else self.open();
          return; // block the native navigation to /cart
        }
      } catch (e) {}
      return _submit.apply(this, arguments);
    };
  };

  JoonCartDrawer.prototype.bind = function () {
    var self = this;

    $all("[data-jcd-close]", this.root).forEach(function (el) {
      el.addEventListener("click", function (e) { e.preventDefault(); self.close(); });
    });
    document.addEventListener("keydown", function (e) { if (e.key === "Escape") self.close(); });

    // Open on cart-icon click (theme-specific selector from settings).
    // Registered on WINDOW capture (earliest phase) + stopImmediatePropagation
    // so the theme's / GoKwik's own cart-click handler (which does
    // location.href = '/cart') never runs. document-capture + preventDefault
    // alone was not enough — a JS handler still redirected to the cart page.
    var openSel = this.cfg.openSelector || 'a.cart[href$="/cart"], [data-jcd-open]';
    var openHandler = function (e) {
      if (self.cfg.enabled === false) return;
      var trigger = e.target && e.target.closest ? e.target.closest(openSel) : null;
      if (!trigger) return;
      e.preventDefault();
      if (e.stopImmediatePropagation) e.stopImmediatePropagation();
      else if (e.stopPropagation) e.stopPropagation();
      self.open();
    };
    window.addEventListener("click", openHandler, true);
    document.addEventListener("click", openHandler, true);

    // Intercept add-to-cart FORM submits (capture) and replace the theme's
    // own add/popup so our drawer is the single add-to-cart experience.
    document.addEventListener("submit", function (e) {
      if (self.cfg.enabled === false) return;
      var form = e.target;
      if (!isAddForm(form)) return;
      e.preventDefault();
      if (e.stopImmediatePropagation) e.stopImmediatePropagation();
      self.addFromForm(form);
    }, true);

    // Delegated clicks inside the drawer.
    this.root.addEventListener("click", function (e) {
      var t = e.target.closest("[data-jcd-action]");
      if (!t) return;
      e.preventDefault();
      var action = t.getAttribute("data-jcd-action");
      var key = t.getAttribute("data-jcd-key");
      var id = t.getAttribute("data-jcd-id");
      if (action === "inc") self.changeQty(key, self.qtyOf(key) + 1);
      else if (action === "dec") self.changeQty(key, self.qtyOf(key) - 1);
      else if (action === "remove") self.changeQty(key, 0);
      else if (action === "add-upsell") self.addById(id, 1);
      else if (action === "checkout") self.checkout();
    });

    this.root.addEventListener("change", function (e) {
      var gift = e.target.closest("[data-jcd-gift]");
      if (gift) self.toggleGift(gift.checked);
    });
  };

  // Safety net: open/refresh the drawer whenever the cart mutates through
  // ANY code path (theme quick-add, apps, etc.), by observing the network.
  JoonCartDrawer.prototype.patchNetwork = function () {
    var self = this;
    var isAdd = function (u) { return /\/cart\/add(\.js)?(\?|$)/.test(u || ""); };
    var isMut = function (u) { return /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(u || ""); };

    if (window.fetch) {
      var _fetch = window.fetch;
      window.fetch = function (input, init) {
        var url = (typeof input === "string") ? input : (input && input.url) || "";
        return _fetch.apply(this, arguments).then(function (resp) {
          if (isMut(url)) {
            setTimeout(function () {
              self.refresh().then(function () { if (isAdd(url) && !self._selfAdd) self.open(); });
            }, 60);
          }
          return resp;
        });
      };
    }
    var _open = XMLHttpRequest.prototype.open;
    var _send = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) { this.__jcdUrl = url; return _open.apply(this, arguments); };
    XMLHttpRequest.prototype.send = function () {
      var xhr = this, url = this.__jcdUrl;
      if (isMut(url)) {
        this.addEventListener("load", function () {
          setTimeout(function () {
            self.refresh().then(function () { if (isAdd(url) && !self._selfAdd) self.open(); });
          }, 60);
        });
      }
      return _send.apply(this, arguments);
    };
  };

  JoonCartDrawer.prototype.qtyOf = function (key) {
    if (!this.cart) return 0;
    var item = this.cart.items.filter(function (i) { return i.key === key; })[0];
    return item ? item.quantity : 0;
  };

  /* ----------------------------- network ----------------------------- */

  JoonCartDrawer.prototype.api = function (url, body) {
    return fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Accept": "application/json" },
      body: JSON.stringify(body)
    }).then(function (r) { return r.json(); });
  };

  JoonCartDrawer.prototype.refresh = function () {
    var self = this;
    return fetch(this.R.get, { headers: { "Accept": "application/json" } })
      .then(function (r) { return r.json(); })
      .then(function (cart) { self.cart = cart; self.render(); return cart; });
  };

  JoonCartDrawer.prototype.addFromForm = function (form) {
    var self = this;
    form.__jcdAt = Date.now();
    var fd = new FormData(form); // includes properties[...] engraving inputs (incl. form= attr)
    self.setBusy(true);
    self._selfAdd = true;
    fetch(this.R.add, { method: "POST", body: fd, headers: { "Accept": "application/json" } })
      .then(function (r) { return r.json(); })
      .then(function () { return self.refresh(); })
      .then(function () { self.setBusy(false); self._selfAdd = false; self.open(); })
      .catch(function () { self.setBusy(false); self._selfAdd = false; });
  };

  JoonCartDrawer.prototype.addById = function (variantId, qty) {
    var self = this;
    self.setBusy(true);
    self._selfAdd = true;
    self.api(this.R.add, { items: [{ id: Number(variantId), quantity: qty || 1 }] })
      .then(function () { return self.refresh(); })
      .then(function () { self.setBusy(false); self._selfAdd = false; })
      .catch(function () { self.setBusy(false); self._selfAdd = false; });
  };

  JoonCartDrawer.prototype.changeQty = function (key, qty) {
    var self = this;
    self.setBusy(true);
    self._selfAdd = true;
    self.api(this.R.change, { id: key, quantity: Math.max(0, qty) })
      .then(function (cart) { self.cart = cart; self.render(); self.setBusy(false); self._selfAdd = false; })
      .catch(function () { self.setBusy(false); self._selfAdd = false; });
  };

  JoonCartDrawer.prototype.toggleGift = function (on) {
    var self = this;
    var giftId = self.cfg.giftVariantId;
    if (!giftId) return;
    if (on) self.addById(giftId, 1);
    else {
      var line = (self.cart.items || []).filter(function (i) { return String(i.variant_id) === String(giftId); })[0];
      if (line) self.changeQty(line.key, 0);
    }
  };

  JoonCartDrawer.prototype.checkout = function () { window.location.href = this.R.checkout; };

  /* ------------------------------ view ------------------------------- */

  JoonCartDrawer.prototype.setBusy = function (b) { this.busy = b; this.root.classList.toggle("jcd--busy", !!b); };

  JoonCartDrawer.prototype.open = function () {
    this.root.classList.add("jcd--open");
    this.root.setAttribute("aria-hidden", "false");
    document.documentElement.classList.add("jcd-lock");
    this.refresh();
  };

  JoonCartDrawer.prototype.close = function () {
    this.root.classList.remove("jcd--open");
    this.root.setAttribute("aria-hidden", "true");
    document.documentElement.classList.remove("jcd-lock");
  };

  JoonCartDrawer.prototype.savings = function () {
    if (!this.cart) return 0;
    return this.cart.items.reduce(function (sum, i) {
      var per = refPrice(i) - (i.final_price || i.price || 0);
      return sum + Math.max(0, per) * i.quantity;
    }, 0);
  };

  // Keep the theme's header cart count in sync.
  JoonCartDrawer.prototype.syncHeader = function () {
    var count = this.cart ? this.cart.item_count : 0;
    var sel = this.cfg.countSelector || 'a.cart[href$="/cart"] > div';
    $all(sel).forEach(function (el) { el.textContent = count; });
    // Update the theme's "Cart (N)" text-link if present.
    $all('a.cart[href$="/cart"] .text-link').forEach(function (el) {
      var base = (el.getAttribute("data-jcd-base") || el.textContent.replace(/\s*\(\d+\)\s*$/, "").trim());
      el.setAttribute("data-jcd-base", base);
      el.textContent = count > 0 ? (base + " (" + count + ")") : base;
    });
  };

  JoonCartDrawer.prototype.render = function () {
    if (!this.cart) return;
    var cfg = this.cfg, cart = this.cart;

    var titleCount = $("[data-jcd-title-count]", this.root);
    if (titleCount) titleCount.textContent = cart.item_count;
    this.syncHeader();

    this.renderProgress();

    if (!cart.items.length) {
      this.itemsEl.innerHTML = '<div class="jcd__empty">' + esc(cfg.emptyText || "Your cart is empty.") + "</div>";
    } else {
      this.itemsEl.innerHTML = cart.items.map(this.itemHtml.bind(this)).join("");
    }

    this.renderFooter();
  };

  JoonCartDrawer.prototype.itemHtml = function (item) {
    var cfg = this.cfg;
    var img = sized(item.image, 160);
    var ref = refPrice(item);
    var final = item.final_price != null ? item.final_price : item.price;
    var onSale = ref > final;
    var price = formatMoney(final, cfg);
    var compare = onSale ? '<span class="jcd__mrp">' + formatMoney(ref, cfg) + "</span>" : "";
    var pct = onSale ? '<span class="jcd__off">(' + Math.round((1 - final / ref) * 100) + "% OFF)</span>" : "";
    var free = final === 0 ? '<span class="jcd__badge-free">FREE</span>' : "";

    var label = (item.variant_title && item.variant_title !== "Default Title") ? item.variant_title : "";
    var variantSel = label ? '<div class="jcd__variant-label">' + esc(label) + "</div>" : "";

    // Line-item properties (e.g. engraving name). Skip hidden `_` keys + blanks.
    var propsHtml = "";
    if (item.properties && typeof item.properties === "object") {
      var rows = Object.keys(item.properties).filter(function (k) {
        return k.charAt(0) !== "_" && item.properties[k] !== "" && item.properties[k] != null;
      }).map(function (k) {
        var v = item.properties[k];
        var vHtml = (typeof v === "string" && v.indexOf("/uploads/") !== -1)
          ? '<a href="' + esc(v) + '" target="_blank">' + esc(v.split("/").pop()) + "</a>"
          : esc(v);
        return '<div class="jcd__prop"><span>' + esc(k) + ":</span> " + vHtml + "</div>";
      });
      if (rows.length) propsHtml = '<div class="jcd__props">' + rows.join("") + "</div>";
    }

    return '' +
      '<div class="jcd__item" data-jcd-line="' + esc(item.key) + '">' +
        '<div class="jcd__thumb"><img loading="lazy" src="' + esc(img) + '" alt="' + esc(item.product_title) + '"></div>' +
        '<div class="jcd__info">' +
          '<div class="jcd__name">' + esc(item.product_title) + free + "</div>" +
          variantSel + propsHtml +
          '<div class="jcd__priceline"><span class="jcd__price">' + price + "</span>" + compare + pct + "</div>" +
          '<div class="jcd__controls">' +
            '<div class="jcd__stepper">' +
              '<button type="button" class="jcd__step" data-jcd-action="dec" data-jcd-key="' + esc(item.key) + '" aria-label="Decrease">−</button>' +
              '<span class="jcd__qty">' + item.quantity + "</span>" +
              '<button type="button" class="jcd__step" data-jcd-action="inc" data-jcd-key="' + esc(item.key) + '" aria-label="Increase">+</button>' +
            "</div>" +
            '<button type="button" class="jcd__remove" data-jcd-action="remove" data-jcd-key="' + esc(item.key) + '" aria-label="Remove">' +
              '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>' +
            "</button>" +
          "</div>" +
        "</div>" +
      "</div>";
  };

  JoonCartDrawer.prototype.renderProgress = function () {
    var cfg = this.cfg, cart = this.cart;
    if (!this.progressEl) return;
    if (!cfg.milestones || !cfg.milestones.length) { this.progressEl.innerHTML = ""; return; }

    var basis = cfg.milestoneBasis === "items" ? cart.item_count : cart.total_price;
    var ms = cfg.milestones.slice().sort(function (a, b) { return a.threshold - b.threshold; });
    var max = ms[ms.length - 1].threshold || 1;
    var pctFill = Math.min(100, (basis / max) * 100);

    var next = ms.filter(function (m) { return basis < m.threshold; })[0];
    var headline;
    if (next) {
      var remaining = next.threshold - basis;
      var remStr = cfg.milestoneBasis === "items"
        ? remaining + (remaining === 1 ? " product" : " products")
        : formatMoney(remaining, cfg);
      headline = (next.headline || "Add {{remaining}} more to unlock {{reward}}")
        .replace(/\{\{\s*remaining\s*\}\}/g, remStr)
        .replace(/\{\{\s*reward\s*\}\}/g, next.reward || "");
    } else {
      headline = cfg.milestoneUnlockedText || "You've unlocked all rewards!";
    }

    var ticks = ms.map(function (m) {
      var met = basis >= m.threshold;
      var pos = Math.min(100, (m.threshold / max) * 100);
      return '<div class="jcd__tick' + (met ? " is-met" : "") + '" style="left:' + pos + '%">' +
        '<span class="jcd__tick-dot"></span><span class="jcd__tick-label">' + esc(m.label || "") + "</span></div>";
    }).join("");

    this.progressEl.innerHTML =
      '<div class="jcd__progress-headline">' + esc(headline) + "</div>" +
      '<div class="jcd__bar"><div class="jcd__bar-fill" style="width:' + pctFill + '%"></div>' + ticks + "</div>";
  };

  JoonCartDrawer.prototype.renderFooter = function () {
    var cfg = this.cfg, cart = this.cart;
    var saved = this.savings();
    $all("[data-jcd-saved]", this.root).forEach(function (el) { el.style.display = saved > 0 ? "" : "none"; });
    $all("[data-jcd-saved-amt]", this.root).forEach(function (el) { el.textContent = formatMoney(saved, cfg); });

    var totalEl = $("[data-jcd-total]", this.root);
    if (totalEl) totalEl.textContent = formatMoney(cart.total_price, cfg);

    var mrp = cart.total_price + saved;
    var mrpEl = $("[data-jcd-mrp]", this.root);
    if (mrpEl) { mrpEl.style.display = saved > 0 ? "" : "none"; if (saved > 0) mrpEl.textContent = formatMoney(mrp, cfg); }
       var offEl = $("[data-jcd-off]", this.root);
    if (offEl) {
      if (saved > 0 && mrp > 0) { offEl.style.display = ""; offEl.textContent = "(" + Math.round((saved / mrp) * 100) + "% OFF)"; }
      else offEl.style.display = "none";
    }

    // Disable checkout if cart total is ₹0
    var checkoutBtn = $("[data-jcd-action='checkout']", this.root);
    var zeroNote = $("[data-jcd-zero-note]", this.root);
    if (checkoutBtn) {
      if (cart.total_price === 0 && cart.item_count > 0) {
        checkoutBtn.disabled = true;
        checkoutBtn.style.opacity = "0.4";
        checkoutBtn.style.cursor = "not-allowed";
        if (zeroNote) zeroNote.style.display = "";
      } else {
        checkoutBtn.disabled = false;
        checkoutBtn.style.opacity = "";
        checkoutBtn.style.cursor = "";
        if (zeroNote) zeroNote.style.display = "none";
      }
    }
  };

  /* ---------------------------- bootstrap ---------------------------- */

  function init() {
    var root = document.getElementById(ROOT_ID);
    if (!root || root.__jcd) return;
    var cfg = readConfig();
    root.__jcd = new JoonCartDrawer(root, cfg);
    window.JoonCart = root.__jcd;
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();
})();
